import { getReasonFromId } from "@/utils/shop.ts";
import type {
  ReturnRequestDto,
  ReturnRequestLineItemDto,
  ShopDto,
} from "@babackapp/private-api-client/types";

export function getDiscountBonusAmount(
  returnRequest: ReturnRequestDto,
  isDone: boolean,
  opt?: { notValidatedByLogistician: boolean },
) {
  const bonus = returnRequest.financial_informations.discount_bonus.bonus;
  if (bonus.type == "fixed_amount") {
    if (returnRequest.financial_informations.discount_bonus.done == isDone) {
      return bonus.amount;
    } else {
      return 0;
    }
  } else if (bonus.type == "percentage") {
    let amount = 0;
    if (isDone) {
      amount = getTotalDiscountHandled(returnRequest, {
        withBonus: true,
        withCharges: true,
        realDiscount: false,
      });
      if (
        isDone &&
        returnRequest.financial_informations.initial_shipping_refunded.done &&
        returnRequest.financial_informations.initial_shipping_refunded.method ==
          "discount"
      ) {
        amount -=
          returnRequest.financial_informations.initial_shipping_refunded.amount;
      }
      amount = amount - amount / (1 + bonus.amount / 100);
    } else {
      amount = _getAmountToDiscountWithoutBonus(returnRequest, opt);
      amount = amount * (bonus.amount / 100);
    }
    return amount;
  }
  return 0;
}

export function getDiscountBonusAmountFromAmount(
  returnRequest: ReturnRequestDto,
  initialAmount: number,
) {
  const bonus = returnRequest.financial_informations.discount_bonus.bonus;
  if (initialAmount <= 0) {
    return 0;
  }

  if (bonus.type === "fixed_amount") {
    return returnRequest.financial_informations.discount_bonus.done
      ? 0
      : bonus.amount;
  } else if (bonus.type === "percentage") {
    return initialAmount * (bonus.amount / 100);
  }

  return 0;
}

export function getTotalDiscountHandled(
  returnRequest: ReturnRequestDto,
  opt?: {
    withCharges: boolean;
    withBonus: boolean;
    realDiscount: boolean;
  },
) {
  if (opt?.realDiscount) {
    return returnRequest.financial_informations.discount.amount;
  }
  let amount = returnRequest.financial_informations.discount.amount;
  if (opt && !opt.withCharges) {
    amount += _getFinancialChargesAmount(returnRequest, "discount", true);
    // on a un plus car les charges ont déjà été soustraites de amount, le montant réelle des discount;
  }
  if (opt && !opt.withBonus) {
    amount -= getDiscountBonusAmount(returnRequest, true);
  }

  return amount;
}

export function getTotalNewOrder({ exchange }: ReturnRequestDto) {
  return exchange.newProductForExchangeCredit.reduce(
    (acc, { price, quantity }) => {
      return acc + price * quantity;
    },
    0,
  );
}

function _getAmountToDiscountWithoutBonus(
  returnRequest: ReturnRequestDto,
  opt?: { notValidatedByLogistician: boolean },
) {
  let amount = 0;
  const lineItemsToDiscount = returnRequest.line_items.filter(
    (li) => li.resolution === "discount",
  );
  amount = lineItemsToDiscount.reduce(
    (prev, li) =>
      prev + getQuantityToHandleMerchant(li, opt) * getPaidPrice(li),
    0,
  );
  return amount;
}

export function getTotalDiscountToHandle(
  returnRequest: ReturnRequestDto,
  opt: {
    withBonus?: boolean;
    withCharges?: boolean;
    notValidatedByLogistician: boolean;
  },
) {
  let amount = _getAmountToDiscountWithoutBonus(returnRequest, {
    notValidatedByLogistician: opt.notValidatedByLogistician,
  });

  if (amount == 0) {
    return 0;
  }

  if (opt?.withBonus) {
    amount += getDiscountBonusAmountFromAmount(returnRequest, amount);
  }
  if (opt?.withCharges) {
    amount -= _getFinancialChargesAmount(returnRequest, "discount", false);
  }

  return Math.round(amount * 100) / 100;
}

export function getTotalToRefund(
  returnRequest: ReturnRequestDto,
  opt: {
    withCharges?: boolean;
    notValidatedByLogistician: boolean;
  },
) {
  let amount = returnRequest.line_items.reduce(
    (prev, li) =>
      prev +
      (li.resolution === "refund"
        ? getQuantityToHandleMerchant(li, {
            notValidatedByLogistician: opt.notValidatedByLogistician,
          }) * getPaidPrice(li)
        : 0),
    0,
  );
  if (opt?.withCharges) {
    amount -= _getFinancialChargesAmount(returnRequest, "refund", false);
  }

  return Math.round(amount * 100) / 100;
}

export function getFinancialChargesList(
  returnRequest: ReturnRequestDto,
  type: "discount" | "refund",
  isDone: boolean,
  opt?: {
    notValidatedByLogistician: boolean;
  },
) {
  const charges = <any>[];
  const fi = returnRequest.financial_informations;
  if (
    fi.return_shipping_paid.method == type &&
    fi.return_shipping_paid.presentment_amount > 0 &&
    fi.return_shipping_paid.done == isDone
  ) {
    charges.push({
      id: "returnShippingPaid",
      presentmentAmount: fi.return_shipping_paid.presentment_amount,
      amount: fi.return_shipping_paid.amount,
      method: fi.return_shipping_paid.method,
      done: fi.return_shipping_paid.done,
    });
  }
  if (
    fi.initial_shipping_refunded.method == type &&
    fi.initial_shipping_refunded.presentment_amount > 0 &&
    fi.initial_shipping_refunded.done == isDone
  ) {
    charges.push({
      id: "initialShippingRefunded",
      sourceAmount: fi.initial_shipping_refunded.sourceAmount,
      presentmentAmount: -1 * fi.initial_shipping_refunded.presentment_amount,
      amount: fi.initial_shipping_refunded.amount,
      method: fi.initial_shipping_refunded.method,
      done: fi.initial_shipping_refunded.done,
    });
  }
  // if (
  //   fi.originalDiscount.method == type &&
  //   fi.originalDiscount.presentmentAmount > 0 &&
  //   ((!isDone &&
  //     fi.originalDiscount.amount < fi.originalDiscount.presentmentAmount) ||
  //     (isDone && fi.originalDiscount.amount > 0))
  // ) {
  //   charges.push({
  //     id: "originalDiscount",
  //     presentmentAmount: isDone
  //       ? fi.originalDiscount.amount
  //       : fi.originalDiscount.presentmentAmount - fi.originalDiscount.amount,
  //     amount: fi.originalDiscount.amount,
  //     method: fi.originalDiscount.method,
  //     done: fi.originalDiscount.done
  //   });
  // }
  if (type == "discount") {
    const discountAmount = getDiscountBonusAmount(returnRequest, isDone, opt);
    if (discountAmount > 0) {
      charges.push({
        id: "discountBonus",
        presentmentAmount: -1 * discountAmount,
      });
    }
  }
  return charges;
}

function _getFinancialChargesAmount(
  returnRequest: ReturnRequestDto,
  type: "discount" | "refund",
  isDone: boolean,
) {
  let charges = 0;
  const fi = returnRequest.financial_informations;
  if (
    fi.return_shipping_paid.method == type &&
    fi.return_shipping_paid.presentment_amount > 0 &&
    fi.return_shipping_paid.done == isDone
  ) {
    charges += fi.return_shipping_paid.presentment_amount;
  }
  if (
    fi.initial_shipping_refunded.method == type &&
    fi.initial_shipping_refunded.presentment_amount > 0 &&
    fi.initial_shipping_refunded.done == isDone
  ) {
    charges -= fi.initial_shipping_refunded.presentment_amount;
  }

  return charges;
}

function getAlertOnReturnRequest(
  returnRequest: ReturnRequestDto,
  shop: ShopDto,
) {
  const quantityByReasonId: {
    [id: string]: { quantity: number; reasonId: string };
  } = {};
  returnRequest.line_items
    .filter(
      (li) =>
        getReasonFromId(li.reasonId, shop)?.rules.highlighted.available == true,
    )
    .forEach((li) => {
      if (!quantityByReasonId[li.reasonId]) {
        quantityByReasonId[li.reasonId] = {
          quantity: 0,
          reasonId: li.reasonId,
        };
      }
      quantityByReasonId[li.reasonId].quantity += li.quantity;
    });
  return {
    reasons: Object.values(quantityByReasonId),
    errors: [],
  };
}

export function getTotalRefunded(
  returnRequest: ReturnRequestDto,
  opt: { withCharges: boolean; realRefund?: boolean },
) {
  if (opt && opt.realRefund) {
    return returnRequest.financial_informations.refund.amount;
  }

  let amount = returnRequest.line_items
    .map((li) =>
      li.resolution == "refund"
        ? getQuantityHandledMerchant(li, { status: "ok" }) * getPaidPrice(li)
        : 0,
    )
    .reduce((prev, current) => prev.valueOf() + current.valueOf(), 0);

  if (opt && opt.withCharges) {
    amount = amount - _getFinancialChargesAmount(returnRequest, "refund", true);
  }

  return amount;
}

export function getQuantityToHandleMerchant(
  lineItem: ReturnRequestLineItemDto,
  opt?: { notValidatedByLogistician: boolean },
): number {
  const baseQuantity =
    opt && opt.notValidatedByLogistician
      ? lineItem.quantity
      : getQuantityHandledLogistician(lineItem, { status: "ok" });
  const alreadyHandled = lineItem.merchant_informations.reduce(
    (previousValue, currentValue) =>
      previousValue + currentValue.quantity.valueOf(),
    0,
  );
  return Math.max(baseQuantity - alreadyHandled, 0);
}

export function getQuantityHandledMerchant(
  lineItem: ReturnRequestLineItemDto,
  opt?: { status: "ok" | "rejected" },
): number {
  const quantity = lineItem.merchant_informations?.reduce(
    (previousValue, currentValue) => {
      if (opt) {
        if (opt.status === currentValue.status) {
          return previousValue + currentValue.quantity.valueOf();
        }
      }
      return previousValue;
    },
    0,
  );
  return quantity;
}

export function getQuantityHandledLogistician(
  lineItem: ReturnRequestLineItemDto,
  opt?: { status: "ok" | "rejected" },
): number {
  return lineItem.logistician_informations?.reduce(
    (previousValue, currentValue) => {
      if (opt) {
        if (opt.status == currentValue.status) {
          return previousValue + currentValue.quantity.valueOf();
        }
      }
      return previousValue;
    },
    0,
  );
}

export function getQuantityToHandleLogistician(
  lineItem: ReturnRequestLineItemDto,
): number {
  const alreadyHandledByLog = lineItem.logistician_informations?.reduce(
    (previousValue, currentValue) =>
      previousValue + currentValue.quantity.valueOf(),
    0,
  );

  const alreadyHandledByMerchant = lineItem.merchant_informations?.reduce(
    (previousValue, currentValue) =>
      previousValue + currentValue.quantity.valueOf(),
    0,
  );
  return (
    lineItem.quantity - Math.max(alreadyHandledByLog, alreadyHandledByMerchant)
  );
}
function getQuantityNotToHandleLogistician(
  lineItem: ReturnRequestLineItemDto,
): number {
  const alreadyHandledByLog = lineItem.logistician_informations?.reduce(
    (previousValue, currentValue) =>
      previousValue + currentValue.quantity.valueOf(),
    0,
  );

  const alreadyHandledByMerchant = lineItem.merchant_informations?.reduce(
    (previousValue, currentValue) =>
      previousValue + currentValue.quantity.valueOf(),
    0,
  );
  return (
    lineItem.quantity - Math.max(alreadyHandledByLog, alreadyHandledByMerchant)
  );
}

function getPaidPrice(lineItem: ReturnRequestLineItemDto) {
  let price = parseFloat(lineItem.price);
  lineItem.unitTaxes.forEach((tax) => {
    if (!tax.includedInPrice) {
      price += tax.amount;
    }
  });
  lineItem.discountsAllocated.forEach((discount) => {
    if (discount.allocationMethod == "EACH") {
      price -= discount.amount / lineItem.quantity_bought;
    } else if (discount.allocationMethod == "ACROSS") {
      price -= discount.amount / lineItem.quantity_bought;
    }
  });
  price = Math.round((price + Number.EPSILON) * 100) / 100;
  return price;
}

function getInitialShippingRefundAmount(
  returnRequest: ReturnRequestDto,
  shop: ShopDto,
) {
  let refund = 0;
  if (returnRequest.financial_informations.initial_shipping_refunded.done) {
    return 0;
  }
  if (shop.preferences.refundInitialShippingRule == "never") {
    refund = 0;
  } else if (shop.preferences.refundInitialShippingRule == "firstLineItem") {
    refund = returnRequest.financial_informations.initial_shipping_refunded.done
      ? returnRequest.financial_informations.initial_shipping_refunded.amount
      : returnRequest.financial_informations.initial_shipping_refunded
          .presentment_amount;
  } else if (shop.preferences.refundInitialShippingRule == "lastLineItem") {
    if (returnRequest.financial_informations.initial_shipping_refunded.done) {
      refund =
        returnRequest.financial_informations.initial_shipping_refunded.amount;
    } else {
      if (
        !returnRequest.line_items.some((item) => {
          if (item.quantity != item.quantity_bought) {
            return true;
          } else {
            return false;
          }
        })
      ) {
        refund =
          returnRequest.financial_informations.initial_shipping_refunded
            .presentment_amount;
      }
    }
  }
  return refund;
}

function getMongoFilterForCurrentStatus(currentStatusNamesToSearch: string[]): {
  $or: any[];
} {
  const statusList = [
    "created",
    "approved",
    "waiting",
    "shipping",
    "shipped",
    "partiallyTreatedLogistician",
    "treatedLogistician",
    "partiallyTreatedMerchant",
    "treatedMerchant",
    "resolved",
    "error",
  ];
  const res: { $or: any[] } = { $or: [] };
  currentStatusNamesToSearch.forEach((statusName) => {
    const statusIndex = statusList.indexOf(statusName);
    const statusArray = [
      {
        ["status." + statusName + ".done"]: true,
      },
    ];
    for (let i = 0; i < statusList.length; i++) {
      if (i > statusIndex) {
        statusArray.push({
          ["status." + statusList[i] + ".done"]: false,
        });
      }
    }
    res.$or.push({ $and: statusArray });
  });
  return res;
}
function getInlineStatusFromReturnRequest(returnRequest: ReturnRequestDto) {
  let status = "";
  for (const key in returnRequest.status) {
    if (returnRequest.status.hasOwnProperty(key)) {
      if (returnRequest.status[key as keyof typeof returnRequest.status].done) {
        status = key;
      }
    }
  }
  return status;
}
